/*
 * Wire types shared by the whole SPA. They mirror docs/ARCHITECTURE.md §4.5–§4.10.
 * Module-specific DTOs live in each module's `api.ts`.
 */

export type Language = 'ru' | 'en' | 'uk';
export type ThemePreference = 'light' | 'dark' | 'system';

/** ISO-8601 timestamp with offset, as sent by the API. */
export type IsoDateTime = string;

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
}

export interface UserRef {
  id: string;
  fullName: string;
  avatarUrl: string | null;
  position: string | null;
  departmentId?: string | null;
  isOnline?: boolean;
}

export interface FileRef {
  id: string;
  name: string;
  size: number;
  contentType: string;
  url: string;
  thumbnailUrl: string | null;
  /**
   * Where the malware scan stands (docs/modules/files.md "Antivirus scanning"): `pending` while the file waits for the scan (a
   * visitor's file cannot be downloaded yet: 403 `files.scan_pending`), `clean`, `skipped` (not scanned). Absent in older responses.
   */
  scanStatus?: 'skipped' | 'pending' | 'clean' | 'infected';
}

/**
 * How the left menu is arranged (docs/modules/admin.md "Menu", docs/modules/identity.md "Menu"): the order of the items and the ones
 * that are hidden. Kept per person (`CurrentUser.menu`) and as the default for everybody (the public portal settings'
 * `menu`); a person's own arrangement wins over the default until they reset it. An item in neither list (a module added
 * later) shows at its natural place.
 */
export interface MenuLayout {
  /** Item ids in the order they are shown; any subset of the items (the rest keep their natural place). */
  order: string[];
  /** Item ids the menu does not show: they stay in the «Hidden items» entry, in the customise dialog and at their URL. */
  hidden: string[];
}

export interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  avatarUrl: string | null;
  position: string | null;
  departmentId: string | null;
  locale: Language;
  timeZone: string;
  /** Persisted UI theme preference (see PUT /api/auth/me/preferences). */
  theme?: ThemePreference | null;
  /** The person's own arrangement of the left menu (PUT /api/auth/me/menu); null: the portal's default applies. */
  menu?: MenuLayout | null;
  /** Roles held without a limit to departments (the access token carries exactly these). */
  roles: string[];
  isAdmin: boolean;
  /**
   * What the roles give the person *now*, expanded by the server (docs/modules/identity.md "Roles and permissions"): every permission id
   * for an administrator. Absent in answers of an older API — read it through the permission hooks, which treat a missing list as empty.
   */
  permissions?: string[];
  /** Permissions held for some departments only (the server checks the target of each action). */
  scopedPermissions?: string[];
  /** Roles held for some departments only. */
  scopedRoles?: string[];
  /** The account still has an initial password (a new installation's administrator): the shell asks to change it. */
  mustChangePassword?: boolean;
  /** The account uses two-factor authentication. */
  twoFactorEnabled?: boolean;
  /**
   * The administrator policy asks the account for something before anything else: set up two-factor authentication
   * (`two-factor-setup`) or replace an expired password (`password-change`). The API refuses everything else (403
   * `auth.session_restricted`), so the shell shows only that step.
   */
  restriction?: SessionRestriction | null;
  /** The policy requires two-factor authentication of this account; it may set it up until then. */
  twoFactorGraceEndsAt?: IsoDateTime | null;
  /** The password has to be replaced by then (policy: maximum password age). */
  passwordExpiresAt?: IsoDateTime | null;
  /**
   * The account has the administrator role, but the security policy's network allow-list does not cover this address:
   * `roles` and `isAdmin` leave the role out and the person works as an employee until they connect from an allowed network.
   */
  adminRestricted?: boolean;
}

export type SessionRestriction = 'two-factor-setup' | 'password-change';

export interface AuthResponse {
  accessToken: string;
  expiresAt: IsoDateTime;
  user: CurrentUser;
}

/**
 * The answer to a correct password when the account has two-factor authentication: no tokens yet, only the token of the
 * pending second step (valid for a few minutes, bound to this browser, usable once).
 */
export interface TwoFactorChallenge {
  twoFactorRequired: true;
  twoFactorToken: string;
  expiresAt: IsoDateTime;
}

/** `POST /api/auth/login`: a session, or the second step. */
export type LoginResponse = AuthResponse | TwoFactorChallenge;

export const isTwoFactorChallenge = (response: LoginResponse): response is TwoFactorChallenge =>
  'twoFactorRequired' in response && response.twoFactorRequired;

export interface UpdatePreferencesRequest {
  locale?: Language;
  timeZone?: string;
  theme?: ThemePreference;
}

export interface UserListItem {
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string | null;
  position: string | null;
  departmentId: string | null;
  departmentName: string | null;
  isActive: boolean;
  isOnline: boolean;
}

export interface UserProfile extends UserListItem {
  phones: string[];
  /**
   * The date of birth. Its year is personal data: the person themselves, HR and administrators get the real date, everybody
   * else the same day and month in the year 2000 (`birthdayYearHidden`). The UI shows day and month only.
   */
  birthday: string | null;
  birthdayYearHidden?: boolean;
  hireDate: string | null;
  about: string | null;
  managerId: string | null;
  manager: UserRef | null;
  timeZone: string;
  locale: Language;
  roles: string[];
}

export interface DepartmentDto {
  id: string;
  name: string;
  parentId: string | null;
  headUserId: string | null;
  head: UserRef | null;
  memberCount: number;
  sortOrder: number;
}

export type GroupVisibility = 'open' | 'closed' | 'secret';
export type GroupRole = 'owner' | 'moderator' | 'member';

export interface GroupListItem {
  id: string;
  name: string;
  description: string | null;
  avatarUrl: string | null;
  isProject: boolean;
  visibility: GroupVisibility;
  memberCount: number;
  myRole: GroupRole | null;
}

/** Compact group reference (mirrors the backend `GroupRef`). */
export interface GroupRef {
  id: string;
  name: string;
  avatarUrl: string | null;
  isProject: boolean;
}

export interface DepartmentRef {
  id: string;
  name: string;
}

/** Converts a list item / profile into the compact reference used by pickers and avatars. */
export function toUserRef(user: UserListItem | UserProfile | CurrentUser): UserRef {
  return {
    id: user.id,
    fullName: user.fullName,
    avatarUrl: user.avatarUrl,
    position: user.position,
    departmentId: user.departmentId,
    isOnline: 'isOnline' in user ? user.isOnline : undefined,
  };
}
